// The header's search and Expand All / Collapse All (QW-452, FR-LPAY-15, FR-LPAY-17).

export type TSearchScope = 'all' | 'track' | 'licensor' | 'payee';

// Typo-tolerant match, the rule Marc saw in the prototype: the text contains the query, or every
// word of the query appears in it in order with letters missing.
const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
const isSubsequence = (needle: string, haystack: string) =>
  haystack.split('').reduce((i, c) => (c === needle[i] ? i + 1 : i), 0) === needle.length;

export function fuzzyMatch(text: string, query: string): boolean {
  const t = norm(text);
  const q = norm(query);
  if (!q) return true;
  if (t.includes(q)) return true;
  const compact = t.replace(/ /g, '');
  return q.split(' ').every((w) => t.includes(w) || isSubsequence(w, compact));
}

// The licensor rows the search keeps, in the chosen scope.
type TSearchable = { trackTitle?: string; rightsOwnerName?: string; payees?: Array<{ name?: string }> };

export function searchRows<T extends TSearchable>(
  rows: T[],
  query: string,
  scope: TSearchScope,
): T[] {
  if (!query.trim()) return rows;
  const values = (r: T) => {
    const payees = (r.payees || []).map((p) => p.name || '');
    if (scope === 'track') return [r.trackTitle];
    if (scope === 'licensor') return [r.rightsOwnerName];
    if (scope === 'payee') return payees;
    return [r.trackTitle, r.rightsOwnerName, ...payees];
  };
  return rows.filter((r) => values(r).some((v) => fuzzyMatch(v || '', query)));
}

// The button reads Collapse All while any row on screen is open, Expand All when none is; a row
// hidden by the search does not count (QA pass on QW-420).
const anyOpen = <K>(open: Set<K>, keys: K[]) => keys.some((k) => open.has(k));

export function expandAllLabel<K>(open: Set<K>, keys: K[]): 'expandAll' | 'collapseAll' {
  return anyOpen(open, keys) ? 'collapseAll' : 'expandAll';
}

// Acts on the rows on screen; rows hidden by the search keep their state.
export function toggleAll<K>(open: Set<K>, keys: K[]): Set<K> {
  const next = new Set(open);
  if (anyOpen(open, keys)) keys.forEach((k) => next.delete(k));
  else keys.forEach((k) => next.add(k));
  return next;
}
